/**
 * 配送依頼の荷役作業のフォーム値。
 *
 * 積込み・取卸しそれぞれの「作業の委託有無」「予定作業時間(分)」「備考」を持つ。
 * 4本の配送依頼モーダル（作成 / 同時作成 / 未配車編集 / 配車後編集）の
 * フォームスキーマから同一定義を取り込むため、フィールド片として切り出している。
 */

import {
  boolean,
  integer,
  maxValue,
  minValue,
  nullable,
  number,
  pipe,
  string
} from 'valibot'

import { VALIDATION_MESSAGES } from '@/constants/common/validation.constants'

/** 予定作業時間の入力範囲（分）。上限は 24 時間 */
export const WORK_MINUTES_MIN = 0
export const WORK_MINUTES_MAX = 1440

/**
 * 予定作業時間(分)。DB は Int 列のため小数は受け付けない。
 *
 * 荷主マスタの既定値 (#4741) も同じ列制約なので、`create-customer-dto.ts` から
 * これを取り込む。範囲を二重定義すると、片方だけ直したときに BE の
 * `@Min(0) @Max(1440)` と食い違い、画面では通るのに保存が 400 になる。
 */
export const workMinutesSchema = nullable(
  pipe(
    number(),
    integer(VALIDATION_MESSAGES.INTEGER),
    minValue(WORK_MINUTES_MIN, VALIDATION_MESSAGES.MIN_VALUE(WORK_MINUTES_MIN)),
    maxValue(WORK_MINUTES_MAX, VALIDATION_MESSAGES.MAX_VALUE(WORK_MINUTES_MAX))
  )
)

// 備考は桁上限を設けない。記載量が運用側で読めないため、既存の配送依頼備考
// (ShipmentOrder.note) と同じ扱いにしている（#4893）。
const workNoteSchema = nullable(string())

/**
 * 荷役作業6項目のスキーマ片。`object({ ...shipmentOrderCargoHandlingFormValueFields })`
 * の形で各フォームスキーマへ展開する。
 *
 * 委託が無でも作業時間や備考を入力しうるため、項目間の相関は検証しない（BE も同様）。
 */
export const shipmentOrderCargoHandlingFormValueFields = {
  /** 積込み作業の委託有無（true=委託あり）。既定は「無」 */
  isLoadingWorkOutsourced: boolean(),
  /** 積込みの予定作業時間(分)。未入力は null */
  loadingWorkMinutes: workMinutesSchema,
  /** 積込み作業の備考。未入力は null */
  loadingWorkNote: workNoteSchema,
  /** 取卸し作業の委託有無（true=委託あり）。既定は「無」 */
  isUnloadingWorkOutsourced: boolean(),
  /** 取卸しの予定作業時間(分)。未入力は null */
  unloadingWorkMinutes: workMinutesSchema,
  /** 取卸し作業の備考。未入力は null */
  unloadingWorkNote: workNoteSchema
}

/** 新規作成時の初期値。既定は「委託なし・時間未入力・備考なし」 */
export const defaultShipmentOrderCargoHandlingFormValue = {
  isLoadingWorkOutsourced: false,
  loadingWorkMinutes: null,
  loadingWorkNote: null,
  isUnloadingWorkOutsourced: false,
  unloadingWorkMinutes: null,
  unloadingWorkNote: null
} as const
